import { DataSampleUnit } from "../types";

export function formatDate(date: Date, minValueText: string | null = "N/A"): string {
	if (typeof (date) !== typeof (Date)) {
		date = new Date(date);
	}
	if (minValueText !== null) {
		if (date.getTime() === 0) {
			return minValueText;
		}
	}
	return date.toLocaleString("nb-NO", {year: "numeric", month: "2-digit", day:"2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false}).replace(",","");
}
export function centerOffsetForPath(pathMarkup: string) {
	const NS = "http://www.w3.org/2000/svg";
	const tmp = document.createElementNS(NS, "svg");
	tmp.setAttribute("xmlns", NS);
	tmp.setAttribute("viewBox", "0 0 250 250");
	Object.assign(tmp.style, {
		position: "absolute",
		left: "-9999px",
		top: "-9999px",
		width: "0",
		height: "0",
		overflow: "visible",
	});

	// Wrap ALL the input markup in a group so we can measure it as one shape
	const g = document.createElementNS(NS, "g");
	g.setAttribute("id", "measure");
	// Safe because we're only measuring known markup we just constructed
	g.innerHTML = pathMarkup;
	tmp.appendChild(g);
	document.body.appendChild(tmp);

	let dx = 0, dy = 0;
	try {
		// Get bbox of the GROUP (not just the first <path>)
		const b = g.getBBox(); // stroke not included; good enough for centering
		const cx = 125, cy = 125; // viewBox center
		dx = cx - (b.x + b.width / 2);
		dy = cy - (b.y + b.height / 2);
	} finally {
		tmp.remove();
	}

	return { dx, dy };
}

export function formatNumbers(value: number, numDigits: number): string {
	return new Intl.NumberFormat("no-NB", {
		maximumFractionDigits: numDigits,
		minimumFractionDigits: numDigits
	}).format(value);
}
export function dateToMs(t: number | string | Date): number {
	if (typeof t === 'number') {
		return t < 1e12 ? Math.round(t * 1000) : Math.round(t);
	}
	const d = t instanceof Date ? t : new Date(t);
	return d.getTime();
}

const SPEED = {
	MS_TO_KTS: 1.9438445,
	MS_TO_MMS: 1000
}

const PRESSURE = {
	MBAR_TO_PA: 100,
	HPA_TO_PA: 100
}
export function getUnitString(unit: DataSampleUnit): string {
	switch (unit) {
		case DataSampleUnit.UNKNOWN:
			return ""
		case DataSampleUnit.METERS_PER_SECOND:
			return "m/s"
		case DataSampleUnit.MILLIMETERS_PER_SECOND:
			return "mm/s"
		case DataSampleUnit.KNOTS:
			return "kts"
		case DataSampleUnit.DEGREE:
			return "°"
		case DataSampleUnit.METERS:
			return "m"
		case DataSampleUnit.CELSIUS:
			return "°C"
		case DataSampleUnit.mBar:
			return "mbar"
		case DataSampleUnit.PERCENTAGE:
			return "%"
		case DataSampleUnit.FAHRENHEIT:
			return "°F"
		case DataSampleUnit.PASCAL:
			return "Pa"
		case DataSampleUnit.HPA:
			return "hPa"
		case DataSampleUnit.MILLIMETER:
			return "mm"
		default:
			return ""
	}
}

export function convertUnits(unitIn: DataSampleUnit, unitOut: DataSampleUnit, valueIn: number | null): number | null {
	if (valueIn === null) {
		return valueIn
	}

	if (unitIn === unitOut) {
		return valueIn
	}

	let baseValue: number | null = null

	switch (unitIn) {
		case DataSampleUnit.METERS_PER_SECOND:
			baseValue = valueIn
			break
		case DataSampleUnit.KNOTS:
			baseValue = valueIn / SPEED.MS_TO_KTS
			break
		case DataSampleUnit.MILLIMETERS_PER_SECOND:
			baseValue = valueIn / SPEED.MS_TO_MMS
			break

		case DataSampleUnit.CELSIUS:
			baseValue = valueIn
			break
		case DataSampleUnit.FAHRENHEIT:
			baseValue = (valueIn - 32) * 5 / 9
			break

		case DataSampleUnit.mBar:
			baseValue = valueIn * PRESSURE.MBAR_TO_PA
			break
		case DataSampleUnit.HPA:
			baseValue = valueIn * PRESSURE.HPA_TO_PA
			break
		case DataSampleUnit.PASCAL:
			baseValue = valueIn
			break

		case DataSampleUnit.METERS:
			baseValue = valueIn
			break
		case DataSampleUnit.MILLIMETER:
			baseValue = valueIn / 1000
			break
	}

	if (baseValue === null) {
		return null
	}

	switch (unitOut) {
		case DataSampleUnit.METERS_PER_SECOND:
			return baseValue
		case DataSampleUnit.KNOTS:
			return Math.round(baseValue * SPEED.MS_TO_KTS)
		case DataSampleUnit.MILLIMETERS_PER_SECOND:
			return baseValue * SPEED.MS_TO_MMS

		case DataSampleUnit.CELSIUS:
			return baseValue
		case DataSampleUnit.FAHRENHEIT:
			return baseValue * 9 / 5 + 32

		case DataSampleUnit.PASCAL:
			return baseValue
		case DataSampleUnit.mBar:
			return baseValue / PRESSURE.MBAR_TO_PA
		case DataSampleUnit.HPA:
			return baseValue / PRESSURE.HPA_TO_PA
		case DataSampleUnit.METERS:
			return baseValue
		case DataSampleUnit.MILLIMETER:
			return baseValue * 1000
	}

	return null
}

export function getHourText(hours: number) {
	return hours === 1 ? "Last hour" : "Last " + hours + " hours";
}